import { Grid2 as Grid, Typography } from '@mui/material';
import { PropsWithChildren, useState } from 'react';
import { useShowContext } from 'framework/show';
import {
  DropDownFilterInputLayout,
  Filter,
  ListFiltersLayout,
  ListItem,
  ListProvider,
  StaticFilters,
  useListContext,
} from 'framework/list';
import { FilterCondition } from 'framework/types';
import { UiThemeOverrider } from 'framework/uiTheme';
import { UpcomingChangeItem, UpcomingChangesType } from './types';
import { UPCOMING_CHANGE_STATUS_OPTIONS, UpcomingChangeStatus, UpcomingChangeType } from './constants';
import { DashboardItemSectionWrapper } from 'framework/ui/dashboard';
import BackupList from './BackupList';
import { StatusFilter } from 'domain/shared/newProject';

type MappedUpcomingChange = UpcomingChangeItem & {
  upcomingChangeId: string;
  type: UpcomingChangeType;
  from: string;
  to?: string;
  status: UpcomingChangeStatus;
  isPrivate: boolean;
};

export const mapChange = (change: UpcomingChangesType): MappedUpcomingChange[] => {
  return change.items.map((item: UpcomingChangeItem) => ({
    upcomingChangeId: change.id,
    type: change.type,
    from: change.plannedStart,
    to: change.plannedEnd,
    status: change.status,
    isPrivate: change.isPrivate,
    ...item,
  }));
};

export const UpcomingChangesItemContent = ({ isTeamTab }: { isTeamTab?: boolean }) => {
  const { data: listData } = useListContext();
  const sections = {
    backup: {
      type: UpcomingChangeType.BACKUP,
      component: BackupList,
    },
    replacement: {
      type: UpcomingChangeType.REPLACEMENT,
      component: BackupList,
    },
  };

  return (
    <>
      {Object.values(sections).map((section, index) => {
        const items = (listData?.items || []).filter(item => item.type === section.type);
        return <section.component key={section.type} items={items} type={section.type} isTeamTab={isTeamTab} />;
      })}
    </>
  );
};

export const ControlledListWithFilter = ({
  data,
  defaultFilters = {},
  onReloadData = () => {},
  children,
}: PropsWithChildren<{ data: ListItem[]; defaultFilters?: Filter; onReloadData?: () => void }>) => {
  const { loading, reloadData } = useListContext();
  const [filters, setFilters] = useState<Filter>(defaultFilters);

  const applyOperator = (fieldValue: any, operator: string, expectedValue: any) => {
    switch (operator) {
      case FilterCondition.IN:
        return expectedValue.includes(fieldValue);
      default:
        return false;
    }
  };

  const filteredData = (): ListItem[] => {
    if (!filters) return data;

    return (
      data?.filter((item: ListItem) => {
        return Object.entries(filters).every(([fieldName, rule]) => {
          const fieldValue = item[fieldName];
          return Object.entries(rule).every(([operator, expectedValue]) =>
            applyOperator(fieldValue, operator, expectedValue),
          );
        });
      }) || []
    );
  };

  const contextValue = {
    data: { items: filteredData() },
    loading,
    reloadData: () => {
      reloadData?.();
      onReloadData();
    },
    filters,
    filterBy: setFilters,
  };

  return (
    <ListProvider value={contextValue}>
      <StaticFilters>{children}</StaticFilters>
    </ListProvider>
  );
};

export const UpcomingChangesFilter = () => {
  return (
    <UiThemeOverrider
      overriddenLayouts={{
        DropdownLayout: DropDownFilterInputLayout,
      }}
    >
      <ListFiltersLayout>
        <StatusFilter source="status" data-testid="status-filter" options={UPCOMING_CHANGE_STATUS_OPTIONS} />
      </ListFiltersLayout>
    </UiThemeOverrider>
  );
};

const UpcomingChangesWrapper = () => {
  const { data: showData } = useShowContext();
  const projectData = showData?.getData?.() ?? {};
  const upcomingChanges = (projectData?.upcomingChanges || []).flatMap((change: UpcomingChangesType) =>
    mapChange(change),
  );

  return (
    <ControlledListWithFilter data={upcomingChanges}>
      <DashboardItemSectionWrapper
        title={
          <Grid container direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Grid size={2.4}>
              <Typography variant="h4" color="primary">
                Upcoming changes
              </Typography>
            </Grid>

            <Grid size="auto" alignItems="center" sx={{ '.filter-item-wrapper': { m: 0 } }}>
              <UpcomingChangesFilter />
            </Grid>

            <Grid size="grow" />
          </Grid>
        }
        defaultExpanded
      >
        <UpcomingChangesItemContent />
      </DashboardItemSectionWrapper>
    </ControlledListWithFilter>
  );
};

export default UpcomingChangesWrapper;
